import { useEffect, useState, useCallback, useRef } from "react";
import Map, {
  Popup,
  Source,
  Layer,
  NavigationControl,
} from "react-map-gl/maplibre";
import { usePlacesStore } from "../stores/placesStore";
import { useUrlFilters } from "../lib/useUrlFilters";
import FilterPanel from "../components/FilterPanel";
import PlacePanel from "../components/PlacePanel";
import PlacesList from "../components/PlacesList";
import ReportOverview, {
  type ReportOverviewRef,
} from "../components/ReportOverview";
import CreatePlanModal from "../components/CreatePlanModal";
import type { Place } from "@hsh/shared";
import "maplibre-gl/dist/maplibre-gl.css";

// County Durham center coordinates
const INITIAL_VIEW = {
  latitude: 54.7761,
  longitude: -1.5733,
  zoom: 10,
};

// Custom hook for detecting mobile viewport
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isMobile;
}

function getScoreColor(score: number): string {
  if (score >= 80) return "#ef4444"; // red - high priority
  if (score >= 60) return "#f97316"; // orange
  if (score >= 40) return "#eab308"; // yellow
  if (score >= 20) return "#22c55e"; // green
  return "#6b7280"; // gray - low priority
}

export default function MapPage() {
  const {
    mapPlaces,
    selectedPlace,
    isLoading,
    fetchPlacesWithFilters,
    fetchMapPlacesWithFilters,
    selectPlace,
    fetchPlace,
  } = usePlacesStore();

  // URL-based state management
  const {
    dataFilters,
    selectedPlaceId,
    view,
    listOpen,
    page,
    setSelectedPlace: setUrlSelectedPlace,
    setView,
    setListOpen,
    activeFilterCount,
  } = useUrlFilters();

  const [viewState, setViewState] = useState(INITIAL_VIEW);
  const [showCreatePlan, setShowCreatePlan] = useState(false);
  const [cursor, setCursor] = useState<string>("grab");
  const mapRef = useRef<any>(null);
  const reportRef = useRef<ReportOverviewRef>(null);
  const isMobile = useIsMobile();

  // Derive UI state from URL
  const showFilters = view === "filters";

  // Open filters panel by default on initial load
  useEffect(() => {
    if (view === undefined) {
      setView("filters");
    }
  }, []); // Only run on mount

  // Callback to refresh report stats and selected place after any data changes
  const handleDataChange = useCallback(async () => {
    // Refresh the selected place to get updated data (new activities, score, etc.)
    if (selectedPlace?._id) {
      await fetchPlace(selectedPlace._id);
    }
    reportRef.current?.refresh();
  }, [selectedPlace?._id, fetchPlace]);

  // Clear selected place when filters change
  useEffect(() => {
    if (selectedPlaceId) {
      selectPlace(null);
      setUrlSelectedPlace(null);
    }
  }, [JSON.stringify(dataFilters)]);

  // Fetch places when filters or page change (from URL)
  useEffect(() => {
    fetchPlacesWithFilters(dataFilters, page);
    fetchMapPlacesWithFilters(dataFilters);
  }, [JSON.stringify(dataFilters), page]);

  // Load selected place from URL on mount or when URL changes
  useEffect(() => {
    if (selectedPlaceId && selectedPlaceId !== selectedPlace?._id) {
      fetchPlace(selectedPlaceId);
    } else if (!selectedPlaceId && selectedPlace) {
      selectPlace(null);
    }
  }, [selectedPlaceId]);

  // Zoom to selected place when it's loaded (including on page refresh)
  useEffect(() => {
    if (selectedPlace?.location?.coordinates) {
      const map = mapRef.current?.getMap();
      if (map) {
        // Only fly if we're zoomed out or far away from the selected place
        const currentZoom = viewState.zoom;
        const targetZoom = Math.max(currentZoom, 15);

        // If already zoomed in enough (above cluster level), just pan without zoom animation
        if (currentZoom >= 13) {
          map.easeTo({
            center: [
              selectedPlace.location.coordinates[0],
              selectedPlace.location.coordinates[1],
            ],
            duration: 300,
          });
        } else {
          map.flyTo({
            center: [
              selectedPlace.location.coordinates[0],
              selectedPlace.location.coordinates[1],
            ],
            zoom: targetZoom,
            duration: 800,
          });
        }
      }
    }
  }, [selectedPlace?._id]);

  // Track if we've done the initial bounds fit
  const hasFitBoundsRef = useRef(false);

  // Fit map bounds to all visible places on initial load
  useEffect(() => {
    // Only fit bounds once on initial load, not on every filter change
    if (hasFitBoundsRef.current) return;
    if (!mapRef.current || mapPlaces.length === 0) return;
    // Don't fit bounds if we have a selected place (we'll zoom to that instead)
    if (selectedPlaceId) return;

    const lngs = mapPlaces
      .map((p) => p.lng)
      .filter((v): v is number => v !== undefined && !isNaN(v));
    const lats = mapPlaces
      .map((p) => p.lat)
      .filter((v): v is number => v !== undefined && !isNaN(v));

    if (lngs.length === 0 || lats.length === 0) return;

    const bounds: [[number, number], [number, number]] = [
      [Math.min(...lngs), Math.min(...lats)],
      [Math.max(...lngs), Math.max(...lats)],
    ];

    mapRef.current.fitBounds(bounds, {
      padding: { top: 50, bottom: 100, left: 50, right: 50 },
      duration: 500,
      maxZoom: 14,
    });

    hasFitBoundsRef.current = true;
  }, [mapPlaces, selectedPlaceId]);

  // Sync selected place to URL when it changes via other means
  useEffect(() => {
    if (selectedPlace?._id && selectedPlace._id !== selectedPlaceId) {
      setUrlSelectedPlace(selectedPlace._id);
    }
  }, [selectedPlace?._id]);

  const handleMarkerClick = useCallback(
    (placeId: string) => {
      // Update URL which will trigger place fetch
      setUrlSelectedPlace(placeId);
    },
    [setUrlSelectedPlace]
  );

  const handleListPlaceClick = useCallback(
    (place: Place) => {
      setUrlSelectedPlace(place._id!);

      // Fly to the place on the map
      const map = mapRef.current?.getMap();
      if (map && place.location?.coordinates) {
        const currentZoom = viewState.zoom;

        // If already zoomed in enough (above cluster level), just pan without zoom animation
        if (currentZoom >= 13) {
          map.easeTo({
            center: [
              place.location.coordinates[0],
              place.location.coordinates[1],
            ],
            duration: 300,
          });
        } else {
          map.flyTo({
            center: [
              place.location.coordinates[0],
              place.location.coordinates[1],
            ],
            zoom: Math.max(currentZoom, 15),
            duration: 800,
          });
        }
      }
    },
    [setUrlSelectedPlace, viewState.zoom]
  );

  const handleClosePlace = useCallback(() => {
    selectPlace(null);
    setUrlSelectedPlace(null);
  }, [selectPlace, setUrlSelectedPlace]);

  const toggleFilters = useCallback(() => {
    setView(showFilters ? null : "filters");
  }, [showFilters, setView]);

  const toggleList = useCallback(() => {
    setListOpen(!listOpen);
  }, [listOpen, setListOpen]);

  const handleMapClick = useCallback(
    (e: any) => {
      // Check if click was on a cluster or marker
      const features = e.features;
      if (features && features.length > 0) {
        const feature = features[0];

        // Handle cluster click - zoom in with smooth animation
        if (feature.properties?.cluster) {
          const [lng, lat] = feature.geometry.coordinates;
          const map = mapRef.current?.getMap();
          if (map) {
            map.flyTo({
              center: [lng, lat],
              zoom: Math.min(viewState.zoom + 2, 18),
              duration: 500,
            });
          }
          return;
        }

        // Handle individual marker click
        if (feature.properties?.placeId) {
          handleMarkerClick(feature.properties.placeId);
          return;
        }
      }
    },
    [handleMarkerClick, viewState.zoom]
  );

  // Convert places to GeoJSON for clustering
  const placesGeoJson = {
    type: "FeatureCollection" as const,
    features: mapPlaces.map((place) => ({
      type: "Feature" as const,
      properties: {
        placeId: place._id,
        name: place.name,
        score: place.score,
        category: place.category,
      },
      geometry: {
        type: "Point" as const,
        coordinates: [place.lng, place.lat],
      },
    })),
  };

  return (
    <div className="flex-1 flex relative h-full w-full">
      {/* Mobile Bottom Navigation Bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-200 px-2 py-2 safe-area-pb">
        <div className="flex items-center justify-around">
          <button
            onClick={toggleFilters}
            className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-colors ${
              showFilters ? "text-primary-600 bg-primary-50" : "text-gray-600"
            }`}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            <span className="text-xs">
              Filters{activeFilterCount > 0 && ` (${activeFilterCount})`}
            </span>
          </button>
          <button
            onClick={toggleList}
            className={`flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-colors ${
              listOpen && !selectedPlace
                ? "text-primary-600 bg-primary-50"
                : "text-gray-600"
            }`}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 10h16M4 14h16M4 18h16"
              />
            </svg>
            <span className="text-xs">List</span>
          </button>
          <button
            onClick={() => setShowCreatePlan(true)}
            className="flex flex-col items-center gap-1 px-4 py-2 rounded-lg text-primary-600"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            <span className="text-xs">Plan</span>
          </button>
        </div>
      </div>

      {/* Filter Panel - Desktop */}
      <div
        className={`hidden md:block absolute top-4 left-4 z-20 transition-transform duration-300 ${
          showFilters ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <FilterPanel onCreatePlan={() => setShowCreatePlan(true)} />
      </div>

      {/* Filter Panel - Mobile (Full Screen Overlay) */}
      {showFilters && isMobile && (
        <div className="fixed inset-0 z-40 bg-white flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
            <button
              onClick={() => setView(null)}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="flex-1 overflow-auto p-4 pb-24">
            <FilterPanel
              onCreatePlan={() => {
                setView(null);
                setShowCreatePlan(true);
              }}
              isMobile
            />
          </div>
        </div>
      )}

      {/* Toggle Filters Button - Desktop */}
      <button
        onClick={toggleFilters}
        className={`hidden md:block absolute top-4 z-10 bg-white rounded-lg shadow-lg p-2 hover:bg-gray-50 transition-all ${
          showFilters ? "left-80" : "left-4"
        }`}
      >
        <svg
          className={`w-5 h-5 text-gray-600 transition-transform ${
            showFilters ? "" : "rotate-180"
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </button>

      {/* Map Controls - Desktop */}
      <div className="hidden md:flex absolute top-4 right-4 z-10 flex-row gap-2">
        <button
          onClick={toggleList}
          className={`bg-white rounded-lg shadow-lg px-3 py-2 text-sm font-medium transition-colors ${
            listOpen && !selectedPlace
              ? "text-primary-600 bg-primary-50"
              : "text-gray-600 hover:bg-gray-50"
          }`}
        >
          📋 List View
        </button>
      </div>

      {/* Stats Bar */}
      <div className="absolute bottom-20 md:bottom-4 left-4 z-10 bg-white rounded-lg shadow-lg px-3 py-2 md:px-4 flex items-center gap-2 md:gap-4 text-xs md:text-sm">
        <span className="text-gray-600">
          <span className="font-semibold text-gray-900">
            {mapPlaces.length}
          </span>{" "}
          <span className="hidden sm:inline">places on map</span>
          <span className="sm:hidden">places</span>
        </span>
        {isLoading && (
          <>
            <span className="text-gray-400">|</span>
            <span className="text-primary-600">Loading...</span>
          </>
        )}
      </div>

      {/* Map */}
      <Map
        ref={mapRef}
        {...viewState}
        onMove={(evt) => setViewState(evt.viewState)}
        onClick={handleMapClick}
        onMouseEnter={() => setCursor("pointer")}
        onMouseLeave={() => setCursor("grab")}
        cursor={cursor}
        interactiveLayerIds={["clusters", "unclustered-point"]}
        style={{ width: "100%", height: "100%" }}
        mapStyle="https://basemaps.cartocdn.com/gl/positron-gl-style/style.json"
      >
        <NavigationControl position="bottom-right" />

        {/* Clustered Place Markers */}
        <Source
          id="places-source"
          type="geojson"
          data={placesGeoJson}
          cluster={true}
          clusterMaxZoom={12}
          clusterRadius={50}
        >
          {/* Cluster circles */}
          <Layer
            id="clusters"
            type="circle"
            filter={["has", "point_count"]}
            paint={{
              "circle-color": [
                "step",
                ["get", "point_count"],
                "#6366f1", // indigo for small clusters
                10,
                "#8b5cf6", // violet for medium
                50,
                "#a855f7", // purple for large
                100,
                "#c026d3", // fuchsia for very large
              ],
              "circle-radius": [
                "step",
                ["get", "point_count"],
                18,
                10,
                22,
                50,
                26,
                100,
                30,
              ],
              "circle-stroke-width": 2,
              "circle-stroke-color": "#ffffff",
            }}
          />

          {/* Cluster count labels */}
          <Layer
            id="cluster-count"
            type="symbol"
            filter={["has", "point_count"]}
            layout={{
              "text-field": "{point_count_abbreviated}",
              "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
              "text-size": 12,
            }}
            paint={{
              "text-color": "#ffffff",
            }}
          />

          {/* Individual markers (unclustered) */}
          <Layer
            id="unclustered-point"
            type="circle"
            filter={
              selectedPlace?._id
                ? [
                    "all",
                    ["!", ["has", "point_count"]],
                    ["!=", ["get", "placeId"], selectedPlace._id],
                  ]
                : ["!", ["has", "point_count"]]
            }
            paint={{
              "circle-color": [
                "step",
                ["get", "score"],
                "#6b7280", // gray - low priority (0-19)
                20,
                "#22c55e", // green (20-39)
                40,
                "#eab308", // yellow (40-59)
                60,
                "#f97316", // orange (60-79)
                80,
                "#ef4444", // red - high priority (80-100)
              ],
              "circle-radius": 8,
              "circle-stroke-width": 2,
              "circle-stroke-color": "#ffffff",
            }}
          />
        </Source>

        {/* Selected Place Popup */}
        {selectedPlace && (
          <Popup
            latitude={selectedPlace.location.coordinates[1]}
            longitude={selectedPlace.location.coordinates[0]}
            anchor="bottom"
            onClose={handleClosePlace}
            closeButton={true}
            closeOnClick={false}
            maxWidth="320px"
          >
            <div className="p-3 min-w-[280px]">
              <h3 className="font-semibold text-gray-900 mb-1">
                {selectedPlace.name}
              </h3>
              <p className="text-sm text-gray-600 mb-2">
                {selectedPlace.address}
              </p>
              <div className="flex items-center gap-2">
                <span
                  className="px-2 py-0.5 rounded text-xs font-medium text-white"
                  style={{
                    backgroundColor: getScoreColor(selectedPlace.score),
                  }}
                >
                  Score: {selectedPlace.score}
                </span>
                <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
                  {selectedPlace.category}
                </span>
              </div>
            </div>
          </Popup>
        )}
      </Map>

      {/* Place Panel (slide-in from right) - Desktop */}
      {selectedPlace && !isMobile && (
        <div className="absolute top-0 right-0 h-full w-96 bg-white shadow-xl z-20 overflow-hidden flex flex-col">
          <PlacePanel
            place={selectedPlace}
            onClose={handleClosePlace}
            onUpdate={handleDataChange}
          />
        </div>
      )}

      {/* Place Panel - Mobile (Full Screen) */}
      {selectedPlace && isMobile && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col">
          <PlacePanel
            place={selectedPlace}
            onClose={handleClosePlace}
            onUpdate={handleDataChange}
            isMobile
          />
        </div>
      )}

      {/* List View Panel - Desktop */}
      {listOpen && !selectedPlace && !isMobile && (
        <div className="absolute top-0 right-0 h-full w-96 bg-white shadow-xl z-20 overflow-hidden flex flex-col">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Places List</h2>
            <button
              onClick={() => setListOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="flex-1 overflow-auto">
            <PlacesList onSelectPlace={handleListPlaceClick} />
          </div>
        </div>
      )}

      {/* List View Panel - Mobile (Full Screen) */}
      {listOpen && !selectedPlace && isMobile && (
        <div className="fixed inset-0 z-40 bg-white flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Places List</h2>
            <button
              onClick={() => setListOpen(false)}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="flex-1 overflow-auto pb-20">
            <PlacesList onSelectPlace={handleListPlaceClick} />
          </div>
        </div>
      )}

      {/* Report Overview */}
      <ReportOverview ref={reportRef} />

      {/* Create Plan Modal */}
      {showCreatePlan && (
        <CreatePlanModal onClose={() => setShowCreatePlan(false)} />
      )}
    </div>
  );
}
